import { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useCookies } from 'react-cookie';

import "./styles/LeaveDayTips.scss";

import { NotificationContainer } from "react-notifications";
import AuditLogModal from "./components/auditLogs/AuditLogModal";

import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from "react-router-dom";

import { Actions as ADVISOR_ACTIONS } from "./store/actions/advisorActions";
import { Actions as AUTHENTICATION_ACTIONS } from "./store/actions/authenticationActions";
import { Actions as COMPANY_ACTIONS } from "./store/actions/companyActions";

import LoadingContent from "./components/common/LoadingContent";

import Navbar from "./components/common/Navbar";
import Sidebar from "./components/common/Sidebar";
import CancelLeaveModal from "./components/leave/CancelLeaveModal";
import Login from "./components/login/Login";
import SetPassword from "./components/login/SetPassword";

import BuddyNotifications from "./components/common/BuddyNotifications";
import TimesheetModal from "./components/timesheets/TimesheetModal";

import ErrorBoundary from "./ErrorBoundary";
import SelectACompany from "@/components/company-pages/SelectACompany";
import Advisor from "@/main-components/AdvisorRouter";
import CompanyRouter from "@/main-components/CompanyRouter";
import Admin from "@/main-components/AdminRouter";
import { useState } from "react";
import PaymentHandlingModal from "./components/admin/modals/PaymentHandlingModal";
import BillingEmailModal from "./components/billing/BillingEmailModal";
import StripeDeleteModal from "./components/billing/StripeDeleteModal";
import MFAModal from "./components/common/MFAModal";
import MFASetup from "./components/common/MFASetup";
import NewCompanyModal from "./components/common/company-list/new-company-modal/NewCompanyModal";
import ArchiveModal from "./components/company-settings/ArchiveModal";
import InviteEmployeeModal from "./components/employees/InviteEmployeeModal";
import MFAVerification from "./components/login/MFAVerification";
import Notification from "./components/notifications/Notification";
import Notifications from "./components/notifications/Notifications";
import ClearPayslipModal from "./components/payroll/ClearPayslipModal";
import ReopenPayrollModal from "./components/payroll/ReopenPayrollModal";
import SendPayslipsModal from "./components/payroll/SendPayslipsModal";
import TestConfirmationModal from "./components/payroll/TestConfirmationModal";
import TestInLiveModal from "./components/payroll/TestInLiveModal";
import ImagePreviewModal from "./components/timesheets/ImagePreviewModal";
import TimesheetCommentModal from "./components/timesheets/TimesheetCommentModal";
import WorkLocationModal from "./components/workLocation/WorkLocationModal";
import GlobalUserNotificationModal from "./components/admin/modals/GlobalUserNotificationModal";
import TransactionHistoryModal from "./components/admin/modals/TransactionHistoryModal";

const DEFAULT_PATH = "/organisation/people";

function App({
	isLoggedIn,
	loggedIn,
	showSidebar,
	companySettings,
	switchCompany,
	loginStatus,
	hasMFA,
	mfaExpires,
	isAdmin,
	getOrganisations,
	advisorId,
	accountStatus,
	getJurisdictions,
	mfaRequired,
	getCompanySettings,
	isAdvisor,
	isSuspended,
	getAdvisorSettings,
	jurisdictions
}) {
	const [currentlyOn, setCurrentlyOn] = useState("");
	const [onAdvisor, onAdmin, onCompany] = useMemo(() => {
		const onAdvisor = currentlyOn === "Advisor";
		const onAdmin = currentlyOn === "Admin";
		const onCompany = currentlyOn === "Company";

		return [onAdvisor, onAdmin, onCompany];
	}, [currentlyOn]);

	const pageClass = `${
		onCompany && isSuspended ? false : showSidebar ? `sidebar-l sidebar-o sidebar-o-xs` : ``
	} side-scroll header-navbar-fixed`;

	const [cookies] = useCookies(["sessionExpiry"]);

	useEffect(() => {
		if (!loggedIn || !cookies.sessionExpiry) isLoggedIn();
		if (loggedIn) getJurisdictions();
	}, [loggedIn, cookies?.sessionExpiry]);

	const defaultPath = useMemo(() => {
		return DEFAULT_PATH;
	}, [accountStatus]);

	useEffect(() => {
		if (!loggedIn || mfaRequired || !jurisdictions) return;
		getOrganisations();
		if (advisorId) getAdvisorSettings(advisorId);
		getCompanySettings();
	}, [loggedIn, advisorId, !mfaRequired, JSON.stringify(jurisdictions)]);

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const orgParams = urlParams.get("organisation");
		// if organisation params found in url attempt to switch to company
		if (orgParams) {
			switchCompany(orgParams, `${window.location.pathname}${urlParams.toString() ? "?" : ""}${urlParams.toString()}`);
			urlParams.delete("organisation");
		}

	}, [companySettings?.org]);

	if (!loggedIn) {
		if (window.location.pathname.startsWith("/set/")) {
			return <SetPassword />;
		}
		if (!loginStatus)
			return (
				<div
					style={{
						height: "100vh",
						width: "100%",
						display: "flex",
						placeContent: "center",
						alignItems: "center",
					}}
				>
					<LoadingContent
						style={{ height: "15vh", width: "15vh" }}
						className="animate-spin"
					/>
				</div>
			);
		return <Login />;
	}

	if (loggedIn && !hasMFA && (isAdmin || (!mfaExpires && companySettings?.mfaRequirement))) {
		return (
			<div
				style={{
					height: "100vh",
					width: "100%",
					display: "flex",
					placeContent: "center",
					alignItems: "center",
				}}
			>
				<MFASetup companyRequired={true} />
				<NotificationContainer />
			</div>
		);
	}

	if (loggedIn && mfaRequired) {
		return (
			<>
				<MFAVerification />
				<NotificationContainer />
			</>
		);
	}

	return (
		<ErrorBoundary>
			<Router>
				<LocationHandler setCurrentlyOn={(s) => setCurrentlyOn(s)} />
				<div
					id="page-container"
					className={pageClass}
				>
					<Sidebar />
					<Navbar />
					<main
						id="main-container"
						style={{ minHeight: "272px", height: "100vh" }}
					>
						<BuddyNotifications />
						<Routes>
							<Route
								path="/"
								element={<Navigate to={defaultPath} />}
							/>
							<Route
								exact
								path="notifications/*"
								element={
									<Routes>
										<Route
											path="/"
											element={<Navigate to="/organisation/notifications" replace />}
										/>
										<Route
											path=":id"
											element={<Notification />}
										/>
									</Routes>
								}
							/>
							<Route
								path="/user/notifications"
								element={<Notifications />}
							/>
							<Route
								exact
								path="/user/organisations"
								element={<SelectACompany />}
							/>

							{isAdmin && (
								<Route
									path="admin/*"
									element={<Admin />}
								/>
							)}

							{isAdvisor ? (
								<>
									<Route
										path="advisor/*"
										element={<Advisor />}
									/>
								</>
							) : null}

							<Route
								path="organisation/*"
								element={<CompanyRouter />}
							/>

							<Route
								path="*"
								element={<DefaultTo />}
							/>
						</Routes>

						<div>
							{/* Modal Container eventually maybe a seprate component?? */}
							<TimesheetModal />
							<AuditLogModal />
							{/* <AttachmentModal /> */}
							{/* <CommentModal /> */}
							<StripeDeleteModal />
							<WorkLocationModal />
							<ReopenPayrollModal />
							<ClearPayslipModal />
							<CancelLeaveModal />
							<InviteEmployeeModal />
							<NotificationContainer />
							<SendPayslipsModal />
							<ImagePreviewModal />
							<MFAModal />
							<NewCompanyModal />
							<TestInLiveModal />
							<TestConfirmationModal />
							<ArchiveModal />
							<TimesheetCommentModal />
							<BillingEmailModal />
							<PaymentHandlingModal />
							<GlobalUserNotificationModal />
							<TransactionHistoryModal />
						</div>
					</main>
				</div>
			</Router>
		</ErrorBoundary>
	);
}

function DefaultTo() {
	const path = useLocation().pathname;
	const newPath = (() => {
		let newPath = DEFAULT_PATH;
		if (!path.startsWith("/organisation")) newPath = "/organisation" + path;
		if (newPath.includes("leave-management")) newPath = newPath.replace("leave-management", "leave");
		return newPath;
	})();

	return (
		<Navigate
			to={newPath}
			replace
		/>
	);
}

function LocationHandler({ setCurrentlyOn }) {
	const reactDomLocation = useLocation();
	const [location, browserLocation] = useMemo(() => {
		const location = window.location.pathname;
		const browserLocation = reactDomLocation.pathname;
		return [location, browserLocation];
	}, [window.location.pathname]);
	const navigate = useNavigate();

	useEffect(() => {
		if (location !== browserLocation) {
			navigate(location);
		}

		if (location.startsWith("/admin")) {
			setCurrentlyOn("Admin");
		} else if (location.startsWith("/advisor")) {
			setCurrentlyOn("Advisor");
		} else if (location.startsWith("/organisation")) {
			setCurrentlyOn("Company");
		}
	}, [location]);

	return <></>;
}

const mapStateToProps = (state) => {
	const { hasMFA, mfaExpires } = state.my.profile || {};
	return {
		loggedIn: state.my.profile ? true : false,
		loginStatus: state.my.loginStatus,
		isAdmin: state.my?.profile?.isAdmin,
		showSidebar: state.sidebar.show,
		organisations: state.organisations?.filter((organisation) => !organisation.isDemo),
		hasDemo: state.organisations?.find((organisation) => organisation.isDemo),
		companySettings: state.company?.settings,
		accountSwitched: state.accountSwitched,
		creatingDemo: state.creatingDemo,
		hasMFA,
		mfaRequired: hasMFA && (mfaExpires ? mfaExpires <= new Date().toISOString() : true),
		advisorId: state?.my?.profile?.advisorId,
		mfaExpires,
		isAdvisor: state.my?.profile?.isAdvisor,
		accountStatus: state?.company?.settings?.status,
		permissions: state?.permissions?.my?.permissions,
		isDemo: state?.company?.settings?.isDemo,
		isSetup: state?.company?.settings?.status === "Setup",
		isSuspended: state?.company?.settings?.status === "Suspended",
		switchingCompany: state.switchingCompany,
		jurisdictions: state.jurisdictions
	};
};

const mapDispatchToProps = (dispatch) => ({
	getCompanySettings: () => dispatch(COMPANY_ACTIONS.getCompanySettings(true)),
	getOrganisations: () => dispatch(AUTHENTICATION_ACTIONS.getOrganisations()),
	//getProfile: () => dispatch(MYACTIONS.getProfile()),
	isLoggedIn: () => dispatch(AUTHENTICATION_ACTIONS.isLoggedIn()),
	switchCompany: (id, link) => dispatch(AUTHENTICATION_ACTIONS.switchCompany(id, link)),
	getJurisdictions: () => dispatch(COMPANY_ACTIONS.getJurisdictions()),
	getAdvisorSettings: (id) => dispatch(ADVISOR_ACTIONS.getAdvisorSettings(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
